import isPlainObject from 'lodash/isPlainObject';
import transform from 'lodash/transform';
import isArray from 'lodash/isArray';
import snakeCase from 'lodash/snakeCase';
import ObjectUtils from '@spothero/utils/object';

type Primitive = string | number | boolean | bigint | symbol | null | undefined;

/**
 * Convert camel case keys to snake case keys
 */
export type CamelToSnake<S extends string> = S extends `${infer T}${infer U}`
    ? U extends Uncapitalize<U>
        ? `${Uncapitalize<T>}${CamelToSnake<U>}`
        : `${Uncapitalize<T>}_${CamelToSnake<Uncapitalize<U>>}`
    : S;

/**
 * Recursively convert object keys to snake_case at the type level
 */
export type DeepSnakeCaseKeys<T> = T extends readonly unknown[]
    ? {[I in keyof T]: DeepSnakeCaseKeys<T[I]>}
    : // Leave primitives, Date, Function, Map, Set as-is
      T extends
            | Primitive
            | Date
            | (() => unknown)
            | Map<never, never>
            | Set<never>
      ? T
      : // Objects: remap keys using CamelToSnake, recurse into values
        T extends object
        ? {
              [K in keyof T as K extends string
                  ? CamelToSnake<K>
                  : K]: DeepSnakeCaseKeys<T[K]>;
          }
        : T;

/**
 * Recursively converts the keys of an object to snake case.
 * @param obj - The object to convert.
 * @returns The object with all keys converted to snake case.
 */
export const convertKeysToSnakeCase = <T, O = T>(
    obj: T
): DeepSnakeCaseKeys<O> => {
    if (isPlainObject(obj)) {
        return transform(
            obj as Record<string, unknown>,
            (result: Record<string, unknown>, value: unknown, key: string) => {
                result[snakeCase(key)] = convertKeysToSnakeCase(value);
            },
            {} as Record<string, unknown>
        ) as unknown as DeepSnakeCaseKeys<O>;
    } else if (isArray(obj)) {
        return (obj as unknown[]).map(
            convertKeysToSnakeCase
        ) as DeepSnakeCaseKeys<O>;
    } else {
        return obj as unknown as DeepSnakeCaseKeys<O>;
    }
};

/**
 * Type level string transform from snake_case to camelCase
 */
export type SnakeToCamel<S extends string> = S extends `${infer H}_${infer T}`
    ? `${Lowercase<H>}${Capitalize<SnakeToCamel<Lowercase<T>>>}`
    : S;

/**
 * Recursively convert object keys from snake_case to camelCase at the type level
 */
export type DeepCamelCaseKeys<T> =
    // Preserve arrays/tuples shape
    T extends readonly unknown[]
        ? {[I in keyof T]: DeepCamelCaseKeys<T[I]>}
        : // Leave primitives, Date, Function, Map, Set as-is
          T extends
                | Primitive
                | Date
                | (() => unknown)
                | Map<never, never>
                | Set<never>
          ? T
          : // Objects: remap keys using SnakeToCamel, recurse into values
            T extends object
            ? {
                  [K in keyof T as K extends string
                      ? SnakeToCamel<K>
                      : K]: DeepCamelCaseKeys<T[K]>;
              }
            : T;

/**
 * Converts the keys of an object to camel case.
 */
export const camelizeKeys = <T, O = T>(data: T): DeepCamelCaseKeys<O> => {
    return ObjectUtils.camelizeKeys(data) as unknown as DeepCamelCaseKeys<O>;
};

/**
 * Checks if a string is in camelCase format
 * @param str The string to check
 * @returns True if the string is in camelCase, false otherwise
 */
const isCamelCase = (str: string): boolean => {
    if (str.length === 0) return false;
    if (!/^[a-z]/.test(str)) return false;
    if (/[ _-]/.test(str)) return false;
    if (!/^[a-zA-Z0-9]+$/.test(str)) return false;

    return true;
};

/**
 * Recursively checks if all keys in an object are camelCase
 * @param obj The object to check
 * @returns {boolean} True if all keys are camelCase, false otherwise
 */
export const areAllKeysCamelCase = (obj: unknown): boolean => {
    if (typeof obj !== 'object' || obj === null) {
        return true;
    }

    if (Array.isArray(obj)) {
        return obj.every((item) => areAllKeysCamelCase(item));
    }

    for (const key of Object.keys(obj)) {
        if (!isCamelCase(key)) {
            return false;
        }

        if (
            typeof obj[key as keyof typeof obj] === 'object' &&
            obj[key as keyof typeof obj] !== null
        ) {
            if (!areAllKeysCamelCase(obj[key as keyof typeof obj])) {
                return false;
            }
        }
    }

    return true;
};

/**
 * Flattens the values of an object into an array, while ensuring that input types are correctly handled.
 * @param obj - The object or value to flatten.
 * @returns The flattened array of values.
 * @example
 * const obj = { a: 1, b: [2, 3], c: { d: 4, e: 5 } };
 * flattenObjectValues(obj); // Returns [1, 2, 3, 4, 5]
 */
export const flattenObjectValues = (obj: unknown): unknown[] => {
    if (
        typeof obj === 'string' ||
        typeof obj === 'number' ||
        typeof obj === 'boolean' ||
        typeof obj === 'symbol' ||
        typeof obj === 'function' ||
        isArray(obj)
    ) {
        console.error(
            `flattenObjectValues error: parameter must be a plain object with values to flatten. Parameter type: ${typeof obj}`
        );

        // If the input is not a plain object, return it wrapped in an array
        return [obj];
    }

    // If the input is a plain object, flatten its values
    if (!obj || Object.values(obj).length === 0) {
        return [];
    }

    return Object.entries(obj).reduce<unknown[]>(
        (acc, [, value]) =>
            Array.isArray(value)
                ? acc.concat(flattenObjectValues(value))
                : typeof value === 'object' && value !== null
                  ? acc.concat(flattenObjectValues(value))
                  : acc.concat(value),
        []
    );
};

export const toSentenceCase = (str: string): string => {
    if (str.length === 0) {
        return str;
    }

    const lowerStr = str.toLowerCase();

    return lowerStr.charAt(0).toUpperCase() + lowerStr.slice(1);
};
